import moment from "moment";
import {
  mapReconciliationAuditShift,
  type StoreReconciliationAuditShiftModel,
} from "./StoreReconciliationAuditsByDateModel";

export interface StoreScratcherAuditWindow {
  reportDate: string;
  startDatetime: string | null;
  endDatetime: string | null;
}

const parseStringOrNull = (value: unknown): string | null => {
  if (value == null) {
    return null;
  }

  const nextValue = String(value).trim();
  return nextValue ? nextValue : null;
};

const combineDateAndTime = (dateValue: unknown, timeValue: unknown): string | null => {
  const date = parseStringOrNull(dateValue);
  const time = parseStringOrNull(timeValue);

  if (!date || !time) {
    return null;
  }

  const parsed = moment(`${date} ${time}`, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm"], true);
  return parsed.isValid() ? parsed.format("YYYY-MM-DDTHH:mm:ss") : null;
};

const getShiftStartDatetime = (audit: any): string | null =>
  parseStringOrNull(audit?.shift_start_datetime) ?? combineDateAndTime(audit?.shift_start_date, audit?.shift_start_time);

const getShiftEndDatetime = (audit: any): string | null =>
  parseStringOrNull(audit?.shift_end_datetime) ?? combineDateAndTime(audit?.shift_end_date, audit?.shift_end_time);

const getSortTime = (value: string | null): number => {
  if (!value) {
    return Number.MAX_SAFE_INTEGER;
  }

  const parsed = moment.parseZone(value);
  return parsed.isValid() ? parsed.valueOf() : Number.MAX_SAFE_INTEGER;
};

const getAuditDisplaySortTime = (audit: any): number => {
  const timestamp = audit?.report_datetime || audit?.audit_created_at || audit?.report_date || audit?.audit_date || 0;
  const parsed = new Date(timestamp).getTime();

  return Number.isFinite(parsed) ? parsed : 0;
};

const formatWindowDateTime = (value: string | null): string | null => {
  if (!value) {
    return null;
  }

  const parsed = moment.parseZone(value);
  return parsed.isValid() ? parsed.format("M/D h:mm A") : null;
};

export default class StoreScratcherReconciliationAuditsRangeModel {
  private readonly _windows: StoreScratcherAuditWindow[];
  private readonly _windowsByDate: Map<string, StoreScratcherAuditWindow>;
  private readonly _shifts: StoreReconciliationAuditShiftModel[];

  constructor(windows: StoreScratcherAuditWindow[], shifts: StoreReconciliationAuditShiftModel[]) {
    this._windows = windows;
    this._windowsByDate = new Map(windows.map((window) => [window.reportDate, window]));
    this._shifts = shifts;
  }

  get shifts(): StoreReconciliationAuditShiftModel[] {
    return this._shifts;
  }

  get count(): number {
    return this._shifts.length;
  }

  getWindow(reportDate: string | null | undefined): StoreScratcherAuditWindow | null {
    if (!reportDate) {
      return null;
    }

    return this._windowsByDate.get(reportDate) ?? null;
  }

  getWindowText(reportDate: string | null | undefined): string {
    const window = this.getWindow(reportDate);
    const start = formatWindowDateTime(window?.startDatetime ?? null);
    const end = formatWindowDateTime(window?.endDatetime ?? null);

    if (!start || !end) {
      return "-";
    }

    return `${start} - ${end}`;
  }

  getRangeWindowText(startDate?: string | null, endDate?: string | null): string {
    const startMoment = startDate ? moment(startDate, "YYYY-MM-DD", true) : null;
    const endMoment = endDate ? moment(endDate, "YYYY-MM-DD", true) : null;
    const rangeWindows = this._windows.filter((window) => {
      const reportDate = moment(window.reportDate, "YYYY-MM-DD", true);

      if (!reportDate.isValid()) {
        return false;
      }

      if (startMoment?.isValid() && reportDate.isBefore(startMoment, "day")) {
        return false;
      }

      if (endMoment?.isValid() && reportDate.isAfter(endMoment, "day")) {
        return false;
      }

      return true;
    });
    const sortedStarts = rangeWindows
      .map((window) => window.startDatetime)
      .filter((value): value is string => Boolean(value))
      .sort((a, b) => getSortTime(a) - getSortTime(b));
    const sortedEnds = rangeWindows
      .map((window) => window.endDatetime)
      .filter((value): value is string => Boolean(value))
      .sort((a, b) => getSortTime(a) - getSortTime(b));
    const start = formatWindowDateTime(sortedStarts[0] ?? null);
    const end = formatWindowDateTime(sortedEnds[sortedEnds.length - 1] ?? null);

    if (!start || !end) {
      return "-";
    }

    return `${start} - ${end}`;
  }

  static fromAuditRows(audits: unknown[]): StoreScratcherReconciliationAuditsRangeModel {
    const groupedByDate = new Map<string, any[]>();

    audits.forEach((audit: any) => {
      const reportDate = parseStringOrNull(audit?.report_date);

      if (!reportDate) {
        return;
      }

      const auditsForDate = groupedByDate.get(reportDate) ?? [];
      auditsForDate.push(audit);
      groupedByDate.set(reportDate, auditsForDate);
    });

    const windows: StoreScratcherAuditWindow[] = Array.from(groupedByDate.entries()).map(([reportDate, auditsForDate]) => {
      const sortedAudits = [...auditsForDate].sort(
        (a, b) => getSortTime(getShiftStartDatetime(a)) - getSortTime(getShiftStartDatetime(b))
      );
      const firstAudit = sortedAudits[0];
      const lastAudit = sortedAudits[sortedAudits.length - 1];

      return {
        reportDate,
        startDatetime: getShiftStartDatetime(firstAudit),
        endDatetime: getShiftEndDatetime(lastAudit),
      };
    });

    const shifts = [...audits]
      .sort((a: any, b: any) => getAuditDisplaySortTime(a) - getAuditDisplaySortTime(b))
      .map((audit: any) => mapReconciliationAuditShift(audit));

    return new StoreScratcherReconciliationAuditsRangeModel(windows, shifts);
  }

  static createEmpty(): StoreScratcherReconciliationAuditsRangeModel {
    return new StoreScratcherReconciliationAuditsRangeModel([], []);
  }
}
